import { Match, Switch, type JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import {
  PropertyType,
  type PropertyChoice,
  type PropertyDefinition,
  type PropertyValue,
} from '@trimble/geospatial';
import { CustomInput, CustomSelect } from '@tcweb/connect-ui';

export interface PropertyFieldProps {
  property: PropertyDefinition;
  value: PropertyValue | undefined;
  onCommit(value: PropertyValue): void;
}

interface BooleanPropertyFieldProps {
  readonly property: PropertyDefinition;
  readonly value: PropertyValue | undefined;
  readonly label: string;
  onCommit(value: boolean): void;
}

const CHOICE_PLACEHOLDER_VALUE = '';

function choiceValue(item: unknown): string {
  return (item as PropertyChoice).value;
}

function choiceLabel(item: unknown): string {
  return (item as PropertyChoice).label;
}

// Empty covers half-typed entries ("-", "1e"), so it clears instead of 0.
function normalizeNumberValue(raw: string): PropertyValue | undefined {
  if (raw.trim() === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

// Text keeps '' as a real value; date and choice cannot represent it.
function normalizeEmptyToNull(raw: string): string | null {
  return raw === '' ? null : raw;
}

function isModusCheckboxChecked(event: Event): boolean {
  const detail = (event as CustomEvent<{ target?: EventTarget | null }>).detail;
  const target = detail?.target;
  return target instanceof HTMLInputElement ? target.checked : false;
}

function BooleanPropertyField(props: BooleanPropertyFieldProps): JSX.Element {
  const inputId = (): string => `feature-property-${props.property.name}`;

  return (
    <div class="tc-custom-field">
      <label for={inputId()}>{props.label}</label>
      <div class="input-focus-group relative flex min-h-8 items-center">
        <modus-wc-checkbox
          attr:data-testid={inputId()}
          class="[&_label]:sr-only"
          inputId={inputId()}
          label={props.label}
          size="sm"
          value={Boolean(props.value)}
          disabled={props.property.readOnly}
          on:inputChange={(event) =>
            props.onCommit(isModusCheckboxChecked(event))
          }
        />
      </div>
    </div>
  );
}

export function PropertyField(props: PropertyFieldProps): JSX.Element {
  const label = (): string =>
    props.property.required
      ? `${props.property.displayName} *`
      : props.property.displayName;

  const stringValue = (): string =>
    props.value === undefined || props.value === null
      ? ''
      : String(props.value);

  const choiceOptions = (): readonly PropertyChoice[] => [
    {
      value: CHOICE_PLACEHOLDER_VALUE,
      label: t('mobileMapping.cadEditing.propertiesPanel.choicePlaceholder'),
    },
    ...(props.property.choices ?? []),
  ];

  const commitIfDefined = (value: PropertyValue | undefined): void => {
    if (value !== undefined) {
      props.onCommit(value);
    }
  };

  return (
    <Switch
      fallback={
        <div class="text-sm text-error">
          {t(
            'mobileMapping.cadEditing.propertiesPanel.unsupportedPropertyType',
            { type: props.property.type }
          )}
        </div>
      }
    >
      <Match when={props.property.type === PropertyType.Boolean}>
        <BooleanPropertyField
          property={props.property}
          value={props.value}
          label={label()}
          onCommit={props.onCommit}
        />
      </Match>
      <Match when={props.property.type === PropertyType.Number}>
        <CustomInput
          name={props.property.name}
          label={label()}
          type="number"
          value={stringValue()}
          disabled={props.property.readOnly}
          required={props.property.required}
          data-cy={`feature-property-${props.property.name}`}
          onChange={(e) =>
            commitIfDefined(normalizeNumberValue(e.currentTarget.value))
          }
        />
      </Match>
      <Match when={props.property.type === PropertyType.Date}>
        <CustomInput
          name={props.property.name}
          label={label()}
          type="date"
          value={stringValue()}
          disabled={props.property.readOnly}
          required={props.property.required}
          data-cy={`feature-property-${props.property.name}`}
          onChange={(e) =>
            props.onCommit(normalizeEmptyToNull(e.currentTarget.value))
          }
        />
      </Match>
      <Match when={props.property.type === PropertyType.Choice}>
        <CustomSelect
          name={props.property.name}
          label={label()}
          options={choiceOptions()}
          getValue={choiceValue}
          getDisplayValue={choiceLabel}
          value={stringValue()}
          disabled={props.property.readOnly}
          data-cy={`feature-property-${props.property.name}`}
          onChange={(e) =>
            props.onCommit(normalizeEmptyToNull(e.currentTarget.value))
          }
        />
      </Match>
      <Match when={props.property.type === PropertyType.Text}>
        <CustomInput
          name={props.property.name}
          label={label()}
          type="text"
          value={stringValue()}
          disabled={props.property.readOnly}
          required={props.property.required}
          data-cy={`feature-property-${props.property.name}`}
          onChange={(e) => props.onCommit(e.currentTarget.value)}
        />
      </Match>
    </Switch>
  );
}
